import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { CheckCircle, ShieldCheck } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { mfaApi } from '../../services/api/mfa'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Label } from '../ui/Label'
import { LoadingSpinner } from '../ui/LoadingSpinner'
import {
  Dialog,
  DialogContent,
  DialogHeader,
} from '../ui/Dialog'
import { DialogHeaderWithIcon } from '../ui/DialogHeaderWithIcon'
import toast from 'react-hot-toast'

interface MfaEnrollDialogProps {
  open: boolean
  onClose: () => void
  onEnrolled: () => void
}

type EnrollStep = 'qr' | 'verify' | 'success'

const verifySchema = z.object({
  code: z
    .string()
    .min(6, 'Enter the 6-digit code')
    .max(6, 'Code must be exactly 6 digits')
    .regex(/^\d{6}$/, 'Code must be 6 digits'),
})

type VerifyFormData = z.infer<typeof verifySchema>

export const MfaEnrollDialog: React.FC<MfaEnrollDialogProps> = ({
  open,
  onClose,
  onEnrolled,
}) => {
  const [step, setStep] = useState<EnrollStep>('qr')
  const [factorId, setFactorId] = useState('')
  const [qrCode, setQrCode] = useState('')
  const [secret, setSecret] = useState('')
  const [loadingEnroll, setLoadingEnroll] = useState(false)
  const [loadingVerify, setLoadingVerify] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    reset: resetForm,
  } = useForm<VerifyFormData>({
    resolver: zodResolver(verifySchema),
  })

  const handleOpen = async () => {
    setLoadingEnroll(true)
    try {
      const { data: factorsData, error: listError } = await supabase.auth.mfa.listFactors()
      if (listError) throw listError
      const stalePending = (factorsData?.totp ?? []).filter((f) => f.status !== 'verified')
      for (const f of stalePending) {
        await supabase.auth.mfa.unenroll({ factorId: f.id })
      }
      const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp' })
      if (error) throw error
      setFactorId(data.id)
      setQrCode(data.totp.qr_code)
      setSecret(data.totp.secret)
      setStep('qr')
      resetForm()
    } catch (error: any) {
      toast.error(error.message || 'Failed to start enrollment')
      onClose()
    } finally {
      setLoadingEnroll(false)
    }
  }

  useEffect(() => {
    if (open) {
      handleOpen()
    }
  }, [open])

  const onSubmit = async (data: VerifyFormData) => {
    setLoadingVerify(true)
    try {
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId,
      })
      if (challengeError) throw challengeError

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code: data.code,
      })

      if (verifyError) {
        setError('code', { message: 'Invalid code. Please try again.' })
        return
      }

      await mfaApi.recordAudit('mfa.enrolled', factorId)
      setStep('success')
    } catch (error: any) {
      toast.error(error.message || 'Verification failed')
    } finally {
      setLoadingVerify(false)
    }
  }

  const handleClose = () => {
    if (step === 'success') {
      onEnrolled()
    }
    onClose()
    setStep('qr')
    setFactorId('')
    setQrCode('')
    setSecret('')
    resetForm()
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogHeaderWithIcon
            icon={ShieldCheck}
            iconColor="teal"
            title="Set up authenticator app"
            description="Scan the QR code or enter the key in your authenticator app, then verify."
          />
        </DialogHeader>

        {loadingEnroll && (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        )}

        {!loadingEnroll && step === 'qr' && (
          <div className="space-y-6">
            <p className="text-sm text-muted-foreground">
              Scan the QR code below with your authenticator app (such as Google Authenticator,
              Authy, or 1Password).
            </p>
            {qrCode && (
              <div
                className="flex justify-center"
                dangerouslySetInnerHTML={{ __html: qrCode }}
                aria-label="QR code for authenticator app setup"
              />
            )}
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Manual entry key
              </p>
              <p className="font-mono text-sm bg-muted rounded-lg p-3 break-all select-all">
                {secret}
              </p>
            </div>
            <Button className="w-full" onClick={() => setStep('verify')}>
              I've scanned the code
            </Button>
          </div>
        )}

        {!loadingEnroll && step === 'verify' && (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <p className="text-sm text-muted-foreground">
              Enter the 6-digit code shown in your authenticator app to confirm setup.
            </p>
            <div className="space-y-2">
              <Label htmlFor="enroll-code">Authentication code</Label>
              <Input
                {...register('code')}
                id="enroll-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="000000"
                maxLength={6}
                className="text-center text-2xl tracking-[0.5em] font-mono"
                autoFocus
              />
              {errors.code && (
                <p className="text-sm text-destructive">{errors.code.message}</p>
              )}
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setStep('qr')}
              >
                Back
              </Button>
              <Button type="submit" className="flex-1" disabled={loadingVerify}>
                {loadingVerify ? (
                  <LoadingSpinner size="sm" variant="icon" className="mr-2" />
                ) : null}
                Verify
              </Button>
            </div>
          </form>
        )}

        {!loadingEnroll && step === 'success' && (
          <div className="flex flex-col items-center gap-4 py-6">
            <div className="h-14 w-14 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <div className="text-center space-y-1">
              <p className="font-semibold text-foreground">Authenticator app enrolled</p>
              <p className="text-sm text-muted-foreground">
                Your account is now protected with two-factor authentication.
              </p>
            </div>
            <Button className="w-full" onClick={handleClose}>
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
